/**
 * Interactive SEO Tools — Shared entry point for all tool landing pages.
 * Bundled by webpack as tools.js and loaded by each tool HTML page.
 */

import {
  generatePassword,
  calculatePasswordStrength,
  getStrengthLabel,
  CHARACTER_SETS,
  PASSWORD_LENGTH_LIMITS,
} from '@/services/password-generator';
import type { PasswordGeneratorConfig } from '@/services/password-generator';

// ==================== Constants ====================

const STRENGTH_COLORS: Record<ReturnType<typeof getStrengthLabel>, string> = {
  'weak': '#ef4444',
  'fair': '#f59e0b',
  'good': '#3b82f6',
  'strong': '#10b981',
  'very strong': '#8b5cf6',
};

const AZURE_SECRET_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9-]*$/;
const AZURE_SECRET_NAME_MAX_LENGTH = 127;
const AZURE_SECRET_NAME_MIN_LENGTH = 1;

const AWS_SECRET_NAME_MAX_LENGTH = 512;
const AWS_SECRET_NAME_MIN_LENGTH = 1;
const AWS_SECRET_NAME_PATTERN = /^[a-zA-Z0-9/_+=.@!-]+$/;

/** Azure Key Vault pricing per 10,000 operations */
const AZURE_PRICING = {
  SOFTWARE_SECRET_PER_10K: 0.03,
  HSM_SECRET_PER_10K: 0.03,
  SOFTWARE_KEY_PER_10K: 0.03,
  HSM_KEY_RSA2048_PER_10K: 1.00,
  HSM_KEY_RSA3072_4096_PER_10K: 5.00,
  HSM_KEY_EC_PER_10K: 5.00,
  CERTIFICATE_RENEWAL_PER: 3.00,
  CERTIFICATE_OPERATIONS_PER_10K: 0.03,
  MANAGED_HSM_POOL_PER_HOUR: 3.20,
} as const;

// ==================== Helpers ====================

/** Safely query a DOM element by ID with type narrowing */
function getElement<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

/** Copy text to clipboard with visual feedback on a button */
function copyToClipboard(text: string, button: HTMLElement): void {
  navigator.clipboard.writeText(text).then(() => {
    const original = button.textContent;
    button.textContent = 'Copied!';
    button.classList.add('copy-success');
    setTimeout(() => {
      button.textContent = original;
      button.classList.remove('copy-success');
    }, 1500);
  }).catch(() => {
    // Fallback for older browsers
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);

    const original = button.textContent;
    button.textContent = 'Copied!';
    button.classList.add('copy-success');
    setTimeout(() => {
      button.textContent = original;
      button.classList.remove('copy-success');
    }, 1500);
  });
}

/** Calculate approximate crack time for display */
function estimateCrackTime(password: string): string {
  let poolSize = 0;
  if (/[a-z]/.test(password)) poolSize += 26;
  if (/[A-Z]/.test(password)) poolSize += 26;
  if (/[0-9]/.test(password)) poolSize += 10;
  if (/[^a-zA-Z0-9]/.test(password)) poolSize += 32;

  if (poolSize === 0) return 'Instant';

  const entropy = password.length * Math.log2(poolSize);
  // Assume 10 billion guesses/second (modern GPU cluster)
  const guessesPerSecond = 1e10;
  const seconds = Math.pow(2, entropy) / guessesPerSecond;

  if (seconds < 1) return 'Instant';
  if (seconds < 60) return `${Math.round(seconds)} seconds`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} minutes`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} hours`;
  if (seconds < 31536000) return `${Math.round(seconds / 86400)} days`;
  if (seconds < 31536000 * 1000) return `${Math.round(seconds / 31536000)} years`;
  if (seconds < 31536000 * 1e6) return `${Math.round(seconds / (31536000 * 1000))}k years`;
  if (seconds < 31536000 * 1e9) return `${Math.round(seconds / (31536000 * 1e6))}M years`;
  return `${(seconds / (31536000 * 1e9)).toExponential(1)} billion years`;
}

/** Calculate entropy in bits */
function calculateEntropy(password: string): number {
  let poolSize = 0;
  if (/[a-z]/.test(password)) poolSize += 26;
  if (/[A-Z]/.test(password)) poolSize += 26;
  if (/[0-9]/.test(password)) poolSize += 10;
  if (/[^a-zA-Z0-9]/.test(password)) poolSize += 32;

  if (poolSize === 0) return 0;
  return Math.round(password.length * Math.log2(poolSize) * 10) / 10;
}

// ==================== Password Generator ====================

function initPasswordGenerator(): void {
  const lengthSlider = getElement<HTMLInputElement>('pwd-length');
  const lengthValue = getElement<HTMLInputElement>('pwd-length-value');
  const uppercaseToggle = getElement<HTMLInputElement>('pwd-uppercase');
  const lowercaseToggle = getElement<HTMLInputElement>('pwd-lowercase');
  const numbersToggle = getElement<HTMLInputElement>('pwd-numbers');
  const specialToggle = getElement<HTMLInputElement>('pwd-special');
  const generateBtn = getElement<HTMLButtonElement>('pwd-generate');
  const copyBtn = getElement<HTMLButtonElement>('pwd-copy');
  const output = getElement<HTMLElement>('pwd-output');
  const strengthBar = getElement<HTMLElement>('pwd-strength-bar');
  const strengthLabel = getElement<HTMLElement>('pwd-strength-label');
  const strengthScore = getElement<HTMLElement>('pwd-strength-score');

  if (!lengthSlider || !output || !generateBtn) return;

  function generate(): void {
    const length = parseInt(lengthSlider!.value, 10);
    const config: PasswordGeneratorConfig = {
      minLength: length,
      maxLength: length,
      includeUppercase: uppercaseToggle?.checked ?? true,
      includeLowercase: lowercaseToggle?.checked ?? true,
      includeNumbers: numbersToggle?.checked ?? true,
      includeSpecial: specialToggle?.checked ?? true,
    };

    const enabledCount = [config.includeUppercase, config.includeLowercase, config.includeNumbers, config.includeSpecial].filter(Boolean).length;
    if (enabledCount === 0) {
      output!.textContent = 'Enable at least one character type';
      return;
    }

    try {
      const password = generatePassword(config);
      output!.textContent = password;
      updateStrengthDisplay(password);
    } catch (e: unknown) {
      output!.textContent = e instanceof Error ? e.message : 'Generation failed';
    }
  }

  function updateStrengthDisplay(password: string): void {
    const score = calculatePasswordStrength(password);
    const label = getStrengthLabel(score);
    const color = STRENGTH_COLORS[label];

    if (strengthBar) {
      strengthBar.style.width = `${score}%`;
      strengthBar.style.backgroundColor = color;
    }
    if (strengthLabel) {
      strengthLabel.textContent = label.charAt(0).toUpperCase() + label.slice(1);
      strengthLabel.style.color = color;
    }
    if (strengthScore) {
      strengthScore.textContent = `${score}/100`;
    }
  }

  lengthSlider.addEventListener('input', () => {
    if (lengthValue) lengthValue.value = lengthSlider.value;
    generate();
  });

  lengthValue?.addEventListener('input', () => {
    const val = Math.min(128, Math.max(4, parseInt(lengthValue.value, 10) || 4));
    lengthSlider.value = String(val);
    generate();
  });

  lengthValue?.addEventListener('blur', () => {
    const val = Math.min(128, Math.max(4, parseInt(lengthValue.value, 10) || 4));
    lengthValue.value = String(val);
    lengthSlider.value = String(val);
  });

  [uppercaseToggle, lowercaseToggle, numbersToggle, specialToggle].forEach(toggle => {
    toggle?.addEventListener('change', generate);
  });

  generateBtn.addEventListener('click', generate);

  copyBtn?.addEventListener('click', () => {
    const text = output!.textContent ?? '';
    if (text && text !== 'Click Generate to create a password' && text !== 'Enable at least one character type') {
      copyToClipboard(text, copyBtn);
    }
  });

  // Generate initial password
  generate();
}

// ==================== Password Strength Checker ====================

function initStrengthChecker(): void {
  const input = getElement<HTMLInputElement>('strength-input');
  const toggleBtn = getElement<HTMLButtonElement>('strength-toggle-visibility');
  const bar = getElement<HTMLElement>('strength-bar');
  const label = getElement<HTMLElement>('strength-label');
  const scoreEl = getElement<HTMLElement>('strength-score');
  const entropyEl = getElement<HTMLElement>('strength-entropy');
  const crackTimeEl = getElement<HTMLElement>('strength-crack-time');
  const tipsEl = getElement<HTMLElement>('strength-tips');
  const charBreakdown = getElement<HTMLElement>('strength-breakdown');

  if (!input) return;

  function analyze(): void {
    const password = input!.value;

    if (!password) {
      if (bar) { bar.style.width = '0%'; bar.style.backgroundColor = '#475569'; }
      if (label) { label.textContent = 'Enter a password'; label.style.color = '#94a3b8'; }
      if (scoreEl) scoreEl.textContent = '—';
      if (entropyEl) entropyEl.textContent = '—';
      if (crackTimeEl) crackTimeEl.textContent = '—';
      if (tipsEl) tipsEl.innerHTML = '';
      if (charBreakdown) charBreakdown.innerHTML = '';
      return;
    }

    const score = calculatePasswordStrength(password);
    const strengthLbl = getStrengthLabel(score);
    const color = STRENGTH_COLORS[strengthLbl];
    const entropy = calculateEntropy(password);
    const crackTime = estimateCrackTime(password);

    if (bar) {
      bar.style.width = `${score}%`;
      bar.style.backgroundColor = color;
    }
    if (label) {
      label.textContent = strengthLbl.charAt(0).toUpperCase() + strengthLbl.slice(1);
      label.style.color = color;
    }
    if (scoreEl) scoreEl.textContent = `${score}/100`;
    if (entropyEl) entropyEl.textContent = `${entropy} bits`;
    if (crackTimeEl) crackTimeEl.textContent = crackTime;

    // Breakdown
    if (charBreakdown) {
      const hasUpper = /[A-Z]/.test(password);
      const hasLower = /[a-z]/.test(password);
      const hasDigit = /[0-9]/.test(password);
      const hasSpecial = /[^a-zA-Z0-9]/.test(password);

      charBreakdown.innerHTML = [
        makeCheckItem('Uppercase (A-Z)', hasUpper),
        makeCheckItem('Lowercase (a-z)', hasLower),
        makeCheckItem('Numbers (0-9)', hasDigit),
        makeCheckItem('Symbols (!@#...)', hasSpecial),
        makeCheckItem('8+ characters', password.length >= 8),
        makeCheckItem('16+ characters', password.length >= 16),
      ].join('');
    }

    // Tips
    if (tipsEl) {
      const tips: string[] = [];
      if (password.length < 12) tips.push('Use at least 12 characters for better security');
      if (!/[A-Z]/.test(password)) tips.push('Add uppercase letters');
      if (!/[a-z]/.test(password)) tips.push('Add lowercase letters');
      if (!/[0-9]/.test(password)) tips.push('Include numbers');
      if (!/[^a-zA-Z0-9]/.test(password)) tips.push('Add special characters (!@#$...)');
      if (password.length >= 16 && score >= 80) tips.push('Great password strength!');

      tipsEl.innerHTML = tips.map(tip =>
        `<div class="flex items-center gap-2 text-sm"><span class="text-amber-400">&#9679;</span><span class="text-slate-300">${tip}</span></div>`
      ).join('');
    }
  }

  toggleBtn?.addEventListener('click', () => {
    const isPassword = input.type === 'password';
    input.type = isPassword ? 'text' : 'password';
    toggleBtn.textContent = isPassword ? 'Hide' : 'Show';
  });

  input.addEventListener('input', analyze);
}

function makeCheckItem(label: string, passed: boolean): string {
  const icon = passed
    ? '<svg class="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>'
    : '<svg class="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>';
  const textColor = passed ? 'text-slate-300' : 'text-slate-500';
  return `<div class="flex items-center gap-2"><span>${icon}</span><span class="${textColor} text-sm">${label}</span></div>`;
}

// ==================== Secret Name Validator ====================

interface ValidationResult {
  readonly valid: boolean;
  readonly errors: readonly string[];
  readonly warnings: readonly string[];
}

function validateAzureSecretName(name: string): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (name.length === 0) {
    return { valid: false, errors: ['Name cannot be empty'], warnings: [] };
  }

  if (name.length > AZURE_SECRET_NAME_MAX_LENGTH) {
    errors.push(`Name exceeds maximum length of ${AZURE_SECRET_NAME_MAX_LENGTH} characters (currently ${name.length})`);
  }

  if (!AZURE_SECRET_NAME_PATTERN.test(name)) {
    errors.push('Must start with a letter and contain only letters, numbers, and hyphens');
  }

  if (name.startsWith('-') || name.endsWith('-')) {
    errors.push('Cannot start or end with a hyphen');
  }

  if (name.includes('--')) {
    warnings.push('Consecutive hyphens are allowed but uncommon');
  }

  if (name.length > 100) {
    warnings.push('Very long names may be harder to work with in CLI tools');
  }

  return { valid: errors.length === 0, errors, warnings };
}

function validateAwsSecretName(name: string): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (name.length === 0) {
    return { valid: false, errors: ['Name cannot be empty'], warnings: [] };
  }

  if (name.length > AWS_SECRET_NAME_MAX_LENGTH) {
    errors.push(`Name exceeds maximum length of ${AWS_SECRET_NAME_MAX_LENGTH} characters (currently ${name.length})`);
  }

  if (!AWS_SECRET_NAME_PATTERN.test(name)) {
    errors.push('Contains invalid characters. Allowed: letters, numbers, /_+=.@!-');
  }

  if (name.startsWith('aws/') || name.startsWith('aws:')) {
    errors.push('Names starting with "aws/" or "aws:" are reserved by AWS');
  }

  if (name.includes('//')) {
    warnings.push('Double slashes are allowed but may cause confusion in ARN paths');
  }

  return { valid: errors.length === 0, errors, warnings };
}

function initSecretNameValidator(): void {
  const input = getElement<HTMLInputElement>('validator-input');
  const providerSelect = getElement<HTMLSelectElement>('validator-provider');
  const resultContainer = getElement<HTMLElement>('validator-result');
  const charCount = getElement<HTMLElement>('validator-char-count');
  const allowedRef = getElement<HTMLElement>('validator-allowed');

  if (!input || !resultContainer) return;

  function updateAllowedChars(): void {
    if (!allowedRef) return;
    const provider = providerSelect?.value ?? 'azure';
    if (provider === 'azure') {
      allowedRef.textContent = 'Letters (a-z, A-Z), numbers (0-9), hyphens (-)';
    } else {
      allowedRef.textContent = 'Letters, numbers, and: / _ + = . @ ! -';
    }
  }

  function validate(): void {
    const name = input!.value;
    const provider = providerSelect?.value ?? 'azure';
    const maxLen = provider === 'azure' ? AZURE_SECRET_NAME_MAX_LENGTH : AWS_SECRET_NAME_MAX_LENGTH;

    if (charCount) {
      charCount.textContent = `${name.length} / ${maxLen}`;
      charCount.className = name.length > maxLen ? 'text-red-400 text-sm' : 'text-slate-400 text-sm';
    }

    if (!name) {
      resultContainer!.innerHTML = '<p class="text-slate-500 text-sm">Type a secret name to validate</p>';
      return;
    }

    const result = provider === 'azure' ? validateAzureSecretName(name) : validateAwsSecretName(name);

    let html = '';

    if (result.valid) {
      html += '<div class="flex items-center gap-2 mb-3"><svg class="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg><span class="text-emerald-400 font-medium">Valid secret name</span></div>';
    } else {
      html += '<div class="flex items-center gap-2 mb-3"><svg class="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg><span class="text-red-400 font-medium">Invalid secret name</span></div>';
    }

    result.errors.forEach(err => {
      html += `<div class="flex items-start gap-2 mb-1"><span class="text-red-400 mt-0.5">&#10005;</span><span class="text-red-300 text-sm">${err}</span></div>`;
    });

    result.warnings.forEach(warn => {
      html += `<div class="flex items-start gap-2 mb-1"><span class="text-amber-400 mt-0.5">&#9888;</span><span class="text-amber-300 text-sm">${warn}</span></div>`;
    });

    resultContainer!.innerHTML = html;
  }

  input.addEventListener('input', validate);
  providerSelect?.addEventListener('change', () => {
    updateAllowedChars();
    validate();
  });

  updateAllowedChars();
}

// ==================== Azure Vault Calculator ====================

function initVaultCalculator(): void {
  const secretsInput = getElement<HTMLInputElement>('calc-secrets');
  const operationsInput = getElement<HTMLInputElement>('calc-operations');
  const keyTypeSelect = getElement<HTMLSelectElement>('calc-key-type');
  const keysInput = getElement<HTMLInputElement>('calc-keys');
  const keyOpsInput = getElement<HTMLInputElement>('calc-key-ops');
  const certsInput = getElement<HTMLInputElement>('calc-certs');
  const certOpsInput = getElement<HTMLInputElement>('calc-cert-ops');
  const resultEl = getElement<HTMLElement>('calc-result');
  const breakdownEl = getElement<HTMLElement>('calc-breakdown');

  if (!operationsInput || !resultEl) return;

  function calculate(): void {
    const secretOps = parseInt(operationsInput!.value, 10) || 0;
    const keyType = keyTypeSelect?.value ?? 'software';
    const keyOps = parseInt(keyOpsInput?.value ?? '0', 10) || 0;
    const certOps = parseInt(certOpsInput?.value ?? '0', 10) || 0;
    const certRenewals = parseInt(certsInput?.value ?? '0', 10) || 0;

    // Secret operations cost
    const secretCost = (secretOps / 10000) * AZURE_PRICING.SOFTWARE_SECRET_PER_10K;

    // Key operations cost
    const keyRateMap: Record<string, number> = {
      'software': AZURE_PRICING.SOFTWARE_KEY_PER_10K,
      'hsm-rsa2048': AZURE_PRICING.HSM_KEY_RSA2048_PER_10K,
      'hsm-rsa3072': AZURE_PRICING.HSM_KEY_RSA3072_4096_PER_10K,
      'hsm-ec': AZURE_PRICING.HSM_KEY_EC_PER_10K,
    };
    const keyRate = keyRateMap[keyType] ?? AZURE_PRICING.SOFTWARE_KEY_PER_10K;
    const keyCost = (keyOps / 10000) * keyRate;

    // Certificate cost
    const certOpsCost = (certOps / 10000) * AZURE_PRICING.CERTIFICATE_OPERATIONS_PER_10K;
    const certRenewalCost = certRenewals * AZURE_PRICING.CERTIFICATE_RENEWAL_PER;

    const totalMonthly = secretCost + keyCost + certOpsCost + certRenewalCost;

    resultEl!.innerHTML = `
      <div class="text-center">
        <p class="text-slate-400 text-sm mb-1">Estimated Monthly Cost</p>
        <p class="text-4xl font-bold text-white">$${totalMonthly.toFixed(2)}</p>
        <p class="text-slate-500 text-xs mt-1">USD / month</p>
      </div>
    `;

    if (breakdownEl) {
      const rows: string[] = [];
      if (secretCost > 0) rows.push(makeBreakdownRow('Secret operations', secretOps, secretCost));
      if (keyCost > 0) rows.push(makeBreakdownRow('Key operations', keyOps, keyCost));
      if (certOpsCost > 0) rows.push(makeBreakdownRow('Certificate operations', certOps, certOpsCost));
      if (certRenewalCost > 0) rows.push(makeBreakdownRow('Certificate renewals', certRenewals, certRenewalCost));
      if (rows.length === 0) rows.push('<p class="text-slate-500 text-sm">Enter operation counts to see breakdown</p>');

      breakdownEl.innerHTML = rows.join('');
    }
  }

  [operationsInput, keyOpsInput, certOpsInput, certsInput].forEach(el => {
    el?.addEventListener('input', calculate);
  });
  keyTypeSelect?.addEventListener('change', calculate);

  calculate();
}

function makeBreakdownRow(label: string, count: number, cost: number): string {
  return `<div class="flex justify-between items-center py-2 border-b border-slate-700/50">
    <div><span class="text-slate-300 text-sm">${label}</span><span class="text-slate-500 text-xs ml-2">(${count.toLocaleString()} ops)</span></div>
    <span class="text-white font-medium">$${cost.toFixed(4)}</span>
  </div>`;
}

// ==================== Cheat Sheet Copy Buttons ====================

function initCheatSheetCopy(): void {
  document.querySelectorAll('[data-copy]').forEach(btn => {
    btn.addEventListener('click', () => {
      const text = (btn as HTMLElement).dataset.copy ?? '';
      copyToClipboard(text, btn as HTMLElement);
    });
  });
}

// ==================== Page Detection & Init ====================

function initTools(): void {
  const page = document.body.dataset.tool;

  switch (page) {
    case 'password-generator':
      initPasswordGenerator();
      break;
    case 'password-strength-checker':
      initStrengthChecker();
      break;
    case 'secret-name-validator':
      initSecretNameValidator();
      break;
    case 'azure-vault-calculator':
      initVaultCalculator();
      break;
    case 'secrets-cheat-sheet':
      initCheatSheetCopy();
      break;
    // azure-vs-aws-secrets is static content, no JS needed
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTools);
} else {
  initTools();
}
